'use client';

/**
 * Revenue Tracking Widget — invoiced amounts and payment counts.
 * Feature: 014-customer-enrolment-platform (Phase 5, T088)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@kit/ui/card';
import { DollarSign, CreditCard, TrendingUp, Receipt } from 'lucide-react';

import type { EnrolmentMetrics } from '../_lib/types';

interface RevenueWidgetProps {
  metrics: EnrolmentMetrics;
}

export function RevenueWidget({ metrics }: RevenueWidgetProps) {
  const hasRevenueData = metrics.revenueInvoiced > 0;
  const avgPerEnrolment =
    hasRevenueData && metrics.totalCreated > 0
      ? metrics.revenueInvoiced / metrics.totalCreated
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="h-4 w-4 text-amber-500" />
          Revenue
        </CardTitle>
        <CardDescription>Invoiced amounts from enrolments</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasRevenueData ? (
          <>
            <div>
              <div className="text-3xl font-bold tabular-nums">
                ${metrics.revenueInvoiced.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Total invoiced</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Receipt className="h-3 w-3" />
                  Invoiced
                </div>
                <p className="text-lg font-semibold tabular-nums">
                  {metrics.totalCreated}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CreditCard className="h-3 w-3" />
                  Paid
                </div>
                <p className="text-lg font-semibold tabular-nums">
                  {metrics.totalConfirmed}
                </p>
              </div>
              {avgPerEnrolment !== null && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    Avg / Enrol
                  </div>
                  <p className="text-lg font-semibold tabular-nums">
                    ${avgPerEnrolment.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
                <CreditCard className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">
                  {metrics.totalPayments}
                </p>
                <p className="text-xs text-muted-foreground">
                  Payments completed
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Revenue data will appear once enrolments with invoice amounts are processed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
